/**
 * "Check your email" — the confirmation body, shared by both callers (DOP-275).
 *
 * The send is fire-and-forget by design: the backend answers 202 whether or
 * not the address has an account, and whether or not it actually mailed
 * anything, so nothing here can tell the user "we sent it" with any more
 * confidence than "we tried". That is what shapes this — it names the address,
 * points at the spam folder, and offers a resend, because those are the three
 * things a user with no mail in their inbox can actually act on.
 *
 * `footer` is the one thing the two callers disagree about: the standalone
 * page offers a way back to login, the Settings dialog a way to close.
 */

import { MailCheck } from "lucide-react";
import React from "react";

export interface ForgotPasswordSentProps {
	/** The address the instructions went to. */
	email: string;
	/** Id for the heading, so the dialog around this can name it. */
	titleId: string;
	/** Seconds left before a resend is allowed; 0 means it is allowed now. */
	cooldownSeconds: number;
	/** A resend is in flight. */
	sending: boolean;
	/** A failed resend, shown here rather than behind the backdrop. */
	error: string;
	onResend: () => void;
	/** What to offer under the resend — differs by where this is shown. */
	footer: React.ReactNode;
}

const ForgotPasswordSent: React.FC<ForgotPasswordSentProps> = ({
	email,
	titleId,
	cooldownSeconds,
	sending,
	error,
	onResend,
	footer,
}) => {
	const resendLabel = sending
		? "Sending..."
		: cooldownSeconds > 0
			? `Resend in ${cooldownSeconds}s`
			: "Resend email";

	return (
		<>
			<MailCheck className="h-8 w-8 text-primary" aria-hidden="true" />

			<h2 id={titleId} className="mt-3 text-lg font-semibold text-text-base">
				Check your email
			</h2>

			<p className="mt-2 text-base text-text-muted">
				We've sent password reset instructions to{" "}
				<strong className="text-text-base">{email}</strong>. It can take a
				minute to arrive — if it isn't there, check your spam folder.
			</p>

			{error && (
				<div className="mt-4 rounded-md border border-red-200 bg-red-50 p-3">
					<p className="text-sm text-red-800">{error}</p>
				</div>
			)}

			<button
				type="button"
				onClick={onResend}
				disabled={cooldownSeconds > 0 || sending}
				className="mt-5 w-full rounded-md border border-gray-300 bg-white px-4 py-3 text-sm font-medium text-text-base transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60"
			>
				{resendLabel}
			</button>

			<div className="mt-4 flex items-center justify-between text-sm">{footer}</div>
		</>
	);
};

export default ForgotPasswordSent;
